import {
  dataKeys,
  dataKeysSLMT_TS,
  fieldKeys,
  fieldKeys_TS,
  one,
  pointtwo,
  pointzerofive,
  three,
  two,
  year2025,
  year2030,
  notAvailable,
  tenlakh,
  dummyNciData,
} from '../../../constants';
import {
  IColumn,
  IDealDetails,
  IDealSummary,
  IGroupNCI,
  IKpiModal,
  TransformedDataEntry,
} from '../../../types/types';
import filterOptions from '../../../constants/filterOptions';
import { 
  BROWNFIELD, 
  GAS_FIRED_GENERATION, 
  GREENFIELD, 
  MCR_AVG_UOM, 
  NATURAL_GAS,
  SLMT_CONVERSION_FACTORS
} from '../../../api/constants';
import {
  getContactInfo,
  getEfficiencyMessage,
  getOperatingModelMessage,
  getPerformanceStandardMessage,
} from '../../KPIContent/setUpMessage';
import { convertValue } from './conversionUtils';
import { MJ_PER_KBBL } from '../../../constants/conversionFactors';
import { roundToDecimals } from '../utils';
import { convertInputToCommaSeparatedWithThreeDecimal } from '../../../utils';

// Debug import log removed

// SLMT Carbon Intensity Calculation (matches calculator logic)
const calculateSLMTCarbonIntensity = (): number => {
  // This matches the calculator's getCarbonIntensityForSLMT logic
  // Fixed carbon intensity value for SLMT based on year
  return 69.9;
};

export const getUniqueTechSource = (dealDetailsData: IDealDetails[]) => {
  return Array.from(
    new Set(
      dealDetailsData
        .map((deal) =>
          deal.TECHNOLOGY_SOURCE_NAME === NATURAL_GAS || deal.TECHNOLOGY_SOURCE_NAME === 'LNG'
            ? GAS_FIRED_GENERATION
            : deal.TECHNOLOGY_SOURCE_NAME,
        )
        .filter((technologyName) => technologyName !== null),
    ),
  );
};

const getSelectedOperatingModel = (OPERATING_MODEL: number) => {
  const { operatingModelOptions } = filterOptions;
  return (
    operatingModelOptions.filter(
      (operatingModel) => operatingModel?.value === Number(OPERATING_MODEL),
    )[0]?.label ?? ''
  );
};

const normalizeText = (value?: string | null) => value ?? '';

const formatCarbonCredit = (carbonCredit?: number) => {
  if (carbonCredit === 1) {
    return 'Yes';
  }
  if (carbonCredit === 0) {
    return 'No';
  }
  return '';
};

const getTechSources = (dealDetailsData: IDealDetails[], tradeCommodityId: number) => {
  return tradeCommodityId === two ? getUniqueTechSource(dealDetailsData) : [];
};

export const getInputFields = (dealDetailsData: IDealDetails[]) => {
  const deal = dealDetailsData[0];
  const {
    EVP_NAME,
    OPPORTUNITY_NAME,
    REGION_NAME,
    COUNTRY_NAME,
    TRADE_COMMODITY_NAME,
    Grid,
    QUANTITY_UNIT,
    GROSS_MARGIN,
    START_DATE,
    END_DATE,
    TRADE_COMMODITY_ID,
    TYPE_OF_SUPPLY_NAME,
    CARBON_CREDIT,
  } = deal;
  
  const uniqueTechSource = getTechSources(dealDetailsData, TRADE_COMMODITY_ID);
  
  // For Gas Fired Generation fields, find the record with populated values
  // instead of using the first record which might be Solar/Hydropower
  const gasFiredRecord = dealDetailsData.find(d => 
    d.FUEL_TYPE || d.ASSET_TYPE || d.OPERATING_MODEL
  );
  
  const ASSET_TYPE = gasFiredRecord?.ASSET_TYPE || deal.ASSET_TYPE;
  const FUEL_TYPE = gasFiredRecord?.FUEL_TYPE || deal.FUEL_TYPE;
  const OPERATING_MODEL = gasFiredRecord?.OPERATING_MODEL || deal.OPERATING_MODEL;
  const EQUITY_SHARE = gasFiredRecord?.EQUITY_SHARE || deal.EQUITY_SHARE;
  const EFFICIENCY = gasFiredRecord?.EFFICIENCY || deal.EFFICIENCY;
  
  const selectedOperatingModel = getSelectedOperatingModel(Number(OPERATING_MODEL));

  // For T&S Products, aggregate all unique products and businesses
  const isTSProducts =
    normalizeText(EVP_NAME)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') === 'tsproducts';

  // Get unique products for T&S Products
  const uniqueProducts = Array.from(
    new Set(dealDetailsData.map((d) => d.PRODUCT_NAME).filter(Boolean)),
  ).join(', ');

  // Get unique businesses
  const uniqueBusinesses = Array.from(
    new Set(dealDetailsData.map((d) => d.BUSINESS_NAME).filter(Boolean)),
  ).join(', ');

  const commodityDisplay = isTSProducts ? uniqueProducts : normalizeText(TRADE_COMMODITY_NAME);
  const businessDisplay = uniqueBusinesses || normalizeText(deal.BUSINESS_NAME);

  return {
    evpName: normalizeText(EVP_NAME),
    opportunityName: normalizeText(OPPORTUNITY_NAME),
    businessName: businessDisplay,
    region: normalizeText(REGION_NAME),
    country: normalizeText(COUNTRY_NAME),
    commodity: commodityDisplay,
    techSource: uniqueTechSource,
    gridLocation: normalizeText(Grid),
    assetType: normalizeText(ASSET_TYPE),
    fuelType: normalizeText(FUEL_TYPE),
    operatingModel: selectedOperatingModel,
    equityShare: EQUITY_SHARE?.toString() ?? '',
    efficiency: EFFICIENCY?.toString() ?? '',
    unitOfMeasure: QUANTITY_UNIT ?? 'MWh',
    grossMargin: GROSS_MARGIN ?? 0,
    startDate: START_DATE ?? '',
    endDate: END_DATE ?? '',
    showGasFields: uniqueTechSource.includes(GAS_FIRED_GENERATION),
    typeOfSupply: normalizeText(TYPE_OF_SUPPLY_NAME), // For SLMT only
    carbonCredit: formatCarbonCredit(CARBON_CREDIT), // For T&S Products only
  };
};

const getTechSourceOrCommodity = (deal: IDealDetails) => {
  const {
    EVP_NAME,
    PRODUCT_NAME,
    TRADE_COMMODITY_ID,
    TRADE_COMMODITY_NAME,
    TECHNOLOGY_SOURCE_NAME,
  } = deal;

  // For T&S Products, return PRODUCT_NAME
  const isTSProducts =
    normalizeText(EVP_NAME)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') === 'tsproducts';
  if (isTSProducts && PRODUCT_NAME) {
    return PRODUCT_NAME;
  }

  // For other EVPs, use existing logic
  const techName = TRADE_COMMODITY_ID === one ? TRADE_COMMODITY_NAME : TECHNOLOGY_SOURCE_NAME;
  return techName?.replace('LNG', 'Gas Fired Generation (LNG)');
};

export const getTableData = (
  dealDetailsData: IDealDetails[],
  cols: { accessorKey: string; header: string }[],
) => {
  const techSource = cols[0].accessorKey;

  // ---------- STEP 1: Deduplicate T&S Products data first ----------
  const isTSProducts = dealDetailsData[0]?.EVP_NAME?.toLowerCase().replace(/[^a-z0-9]/g, '') === 'tsproducts';
  
  // For T&S Products, create consolidated data by removing business ID duplicates
  let consolidatedData = dealDetailsData;
  if (isTSProducts) {
    const uniqueRecords = new Map();
    dealDetailsData.forEach((item) => {
      // Create unique key based on year, quantity, and product (excluding business ID)
      const uniqueKey = `${item.YEAR}-${item.QUANTITY}-${item.PRODUCT_NAME}`;
      
      // Keep only the first occurrence of each unique combination
      if (!uniqueRecords.has(uniqueKey)) {
        uniqueRecords.set(uniqueKey, item);
      }
    });
    consolidatedData = Array.from(uniqueRecords.values());
  }

  // ---------- STEP 2: Transform base table ----------
  const transformedData = consolidatedData.reduce((acc: TransformedDataEntry[], item) => {
    // Determine the row name using the helper function
    const rowName = getTechSourceOrCommodity(item);
    
    // Use original logic for non-duplicated records
    const existingEntryIndex = acc.findIndex((entry) => entry[techSource] === rowName);

    if (existingEntryIndex === -1) {
      // Create new entry
      const newEntry: TransformedDataEntry = { [techSource]: rowName };
      cols.slice(1).forEach((col) => {
        if (String(item.YEAR) === col.accessorKey) {
          newEntry[col.header] = item.QUANTITY;
        } else {
          newEntry[col.header] = 0;
        }
      });
      acc.push(newEntry);
    } else {
      // Update existing entry - sum quantities for the same row/year
      const existingEntry = acc[existingEntryIndex];
      cols.slice(1).forEach((col) => {
        if (String(item.YEAR) === col.accessorKey) {
          const currentValue = existingEntry[col.header] || 0;
          existingEntry[col.header] = Number(currentValue) + Number(item.QUANTITY);
        }
      });
    }

    return acc;
  }, []);

  // ---------- STEP 3: Build Total Volume ----------
  const totalVolume = buildTotalVolumeRow(transformedData, cols, techSource);

  // ---------- STEP 4: Check if carbon credit row applies ----------
  const hasCarbonCredit = consolidatedData.some((d) => d.CARBON_CREDIT === 1);

  if (isTSProducts && hasCarbonCredit) {
    const carbonCreditsRow = buildCarbonCreditRow(consolidatedData, cols, techSource);
    return [...transformedData, totalVolume, carbonCreditsRow];
  }

  return [...transformedData, totalVolume];
};

// --------------------------------------------------
// Helper: Total Volume Row
// --------------------------------------------------
function buildTotalVolumeRow(
  transformedData: TransformedDataEntry[],
  cols: { accessorKey: string; header: string }[],
  techSource: string,
): TransformedDataEntry {
  const totalVolume: TransformedDataEntry = { [techSource]: 'Total Volume' };

  cols.slice(1).forEach((col) => {
    totalVolume[col.header] = transformedData.reduce((sum, entry) => {
      return sum + Number(entry[col.header] ?? 0);
    }, 0);
  });

  return totalVolume;
}

// --------------------------------------------------
// Helper: Carbon Credit Row (T&S Products)
// --------------------------------------------------
function buildCarbonCreditRow(
  consolidatedData: IDealDetails[],
  cols: { accessorKey: string; header: string }[],
  techSource: string,
): TransformedDataEntry {
  const row: TransformedDataEntry = {
    [techSource]: 'Carbon Credits (Carbon Reduction), KTPA',
  };

  cols.slice(1).forEach((col) => {
    const year = parseInt(col.header);
    // Get the record for this year (data is already deduplicated)
    const firstRecord = consolidatedData.find((d) => d.YEAR === year);
    
    // Use carbon credit from the record (data is already consolidated)
    const creditValue = firstRecord?.CARBON_CREDIT_QUANTITY ?? 
      (firstRecord?.CARBON_CREDIT === 1 ? 500 : 0); // Fallback when credits enabled
    row[col.header] = creditValue;
  });

  return row;
}

export const getYears = (dealDetailsData: IDealDetails[]) => {
  const uniqueYears = new Set();

  // Iterate over the data and add each year to the Set
  dealDetailsData.forEach((deal: IDealDetails) => {
    uniqueYears.add(deal.YEAR);
  });
  return Array.from(uniqueYears).map((year) => String(year));
};

export const getMaxValue = (dealDetailsData: IDealDetails[]) => {
  if (!dealDetailsData.length) {
    return { maxQuantity: null, maxAge: null };
  }

  const evpName = dealDetailsData[0]?.EVP_NAME;
  const isTSProducts = evpName?.toLowerCase().replace(/[^a-z0-9]/g, '') === 'tsproducts';

  // For T&S Products, calculate proper NAE values like calculator
  if (isTSProducts) {
    // Deduplicate data first
    const uniqueRecords = new Map();
    dealDetailsData.forEach((item) => {
      const uniqueKey = `${item.YEAR}-${item.QUANTITY}-${item.PRODUCT_NAME}`;
      if (!uniqueRecords.has(uniqueKey)) {
        uniqueRecords.set(uniqueKey, item);
      }
    });
    const consolidatedData = Array.from(uniqueRecords.values());

    // Group data by year and calculate NAE
    const yearlyTotals: Record<number, { equityScope: number; carbonEmissions: number; mcr: number; totalEnergyMJ: number; volumeTimesIntensity: number }> = {};
    
    // Use centralized conversion factors

    consolidatedData.forEach((deal) => {
      const year = deal.YEAR;
      if (!yearlyTotals[year]) {
        yearlyTotals[year] = { 
          equityScope: 0, 
          carbonEmissions: 0, 
          mcr: 0, 
          totalEnergyMJ: 0,
          volumeTimesIntensity: 0 
        };
      }
      
      // Calculate NAE for T&S Products like calculator
      const quantity = deal.QUANTITY ?? 0;
      const carbonIntensity = deal.CARBON_INTENSITY ?? 0; // Keep original CI for volume weighting
      const productName = deal.PRODUCT_NAME ?? '';
      const conversionFactor = MJ_PER_KBBL[productName] || 0;
      
      const energyInMJ = quantity * conversionFactor;
      let nae_KTPA = (energyInMJ * carbonIntensity) / 1_000_000_000;
      
      // Apply carbon credits ONLY to emissions, not to CI for volume weighting
      if (deal.CARBON_CREDIT === 1) {
        const carbonReduction = deal.CARBON_CREDIT_QUANTITY ?? 500;
        nae_KTPA = Math.max(0, nae_KTPA - carbonReduction);
      }
      
      yearlyTotals[year].equityScope += deal.EQUITY_SCOPE ?? 0;
      yearlyTotals[year].carbonEmissions += nae_KTPA; // Use calculated NAE (with credits)
      yearlyTotals[year].totalEnergyMJ += energyInMJ; // Track total energy
      yearlyTotals[year].volumeTimesIntensity += energyInMJ * carbonIntensity; // Use ORIGINAL CI
      yearlyTotals[year].mcr = Math.max(yearlyTotals[year].mcr, deal.MCR ?? 0);
    });

    // Find the maximum across all years
    const years = Object.keys(yearlyTotals).map(Number);
    const maxEquityScope = {
      value: Math.max(...years.map((year) => yearlyTotals[year].equityScope)),
      unit: 'mtCO2e', // Use calculator unit
    };
    const maxNetAbsoluteEmission = {
      value: Math.max(...years.map((year) => yearlyTotals[year].carbonEmissions)) / 1000, // Convert KTPA to display format
      unit: 'mtCO2e', // Use calculator unit (converted from KTPA)
    };
    
    // For T&S Products: Calculate Volume-weighted Average CI (original calculator logic)
    const totalVolumeTimesIntensity = years.reduce((sum, year) => sum + (yearlyTotals[year].volumeTimesIntensity || 0), 0);
    const totalVolume = years.reduce((sum, year) => sum + (yearlyTotals[year].totalEnergyMJ || 0), 0);
    const volumeWeightedAvgCI = totalVolume > 0 ? totalVolumeTimesIntensity / totalVolume : 0;

    const maxMCR = {
      value: volumeWeightedAvgCI,
      unit: MCR_AVG_UOM, // Volume-weighted Average CI unit
    };

    return { maxEquityScope, maxNetAbsoluteEmission, maxMCR };
  } else {
    // Original logic for non-T&S Products
    const yearlyTotals: Record<number, { equityScope: number; carbonEmissions: number; mcr: number }> = {};

    dealDetailsData.forEach((deal) => {
      const year = deal.YEAR;
      if (!yearlyTotals[year]) {
        yearlyTotals[year] = { equityScope: 0, carbonEmissions: 0, mcr: 0 };
      }
      yearlyTotals[year].equityScope += deal.EQUITY_SCOPE ?? 0;
      yearlyTotals[year].carbonEmissions += deal.CARBON_EMISSIONS ?? 0;
      yearlyTotals[year].mcr = Math.max(yearlyTotals[year].mcr, deal.MCR ?? 0);
    });

    // Find the maximum across all years
    const years = Object.keys(yearlyTotals).map(Number);
    const maxEquityScope = {
      value: Math.max(...years.map((year) => yearlyTotals[year].equityScope)),
      unit: dealDetailsData[0].EQUITY_SCOPE_UNIT,
    };
    const maxNetAbsoluteEmission = {
      value: Math.max(...years.map((year) => yearlyTotals[year].carbonEmissions)),
      unit: dealDetailsData[0].CARBON_EMISSIONS_UNIT,
    };
    
    // Calculate MCR like calculator does: grossMargin / totalEmission (sum of all years)
    const grossMarginMillions = dealDetailsData[0]?.GROSS_MARGIN ?? 0;
    const totalEmission = years.reduce((sum, year) => sum + yearlyTotals[year].carbonEmissions, 0);
    const calculatedMCR = totalEmission > 0 ? grossMarginMillions / totalEmission : 0;
    
    const maxMCR = {
      value: calculatedMCR,
      unit: MCR_AVG_UOM,
    };

    return { maxEquityScope, maxNetAbsoluteEmission, maxMCR };
  }
};

const getSummedUpData = (dealDetailsData: IDealDetails[], netCarbonIntensityData: IGroupNCI[]) => {
  const evpName = dealDetailsData[0]?.EVP_NAME;
  const isTSProducts = evpName?.toLowerCase().replace(/[^a-z0-9]/g, '') === 'tsproducts';

  // CRITICAL FIX: For T&S Products, deduplicate business records BEFORE calculations
  let consolidatedData = dealDetailsData;
  if (isTSProducts) {
    const uniqueRecords = new Map();
    dealDetailsData.forEach((item) => {
      // Create unique key based on year, quantity, and product (excluding business ID)
      const uniqueKey = `${item.YEAR}-${item.QUANTITY}-${item.PRODUCT_NAME}`;
      
      // Keep only the first occurrence of each unique combination
      if (!uniqueRecords.has(uniqueKey)) {
        uniqueRecords.set(uniqueKey, item);
      }
    });
    consolidatedData = Array.from(uniqueRecords.values());
  }

  // Product-specific conversion factors from KBBL to MJ (from Excel specification)
    // Use centralized conversion factors

  // CAT11 factors for Scope 3 calculation (Tank to Wheel CI)
  const cat11Factors: Record<string, number> = {
    Gasoline: 71.46,
    Diesel: 82.33,
    Kerosene: 72.54,
    LPG: 62.94,
    'Fuel Oil': 79.02,
    Ethanol: 0.55,
    HVO: 0.24,
    FAME: 1.18,
  };

  function updateExistingYear(acc: IDealSummary[], index: number, current: IDealDetails) {
    const quantity = current.QUANTITY ?? 0;
    const carbonIntensity = current.CARBON_INTENSITY ?? 0;
    const productName = current.PRODUCT_NAME ?? '';

    acc[index].OPERATED_SCOPE += current.OPERATED_SCOPE ?? 0;
    acc[index].EQUITY_SCOPE += current.EQUITY_SCOPE ?? 0;
    
    // Calculate additional NAE for this record
    const conversionFactor = MJ_PER_KBBL[productName] || 0;
    const cat11Factor = cat11Factors[productName] || 0;
    const energyInMJ = quantity * conversionFactor;
    
    // Apply carbon credits effect to carbon intensity (like calculator)
    let effectiveCI = carbonIntensity;
    if (isTSProducts && current.CARBON_CREDIT === 1 && energyInMJ > 0) {
      const carbonCredits = current.CARBON_CREDIT_QUANTITY ?? 500;
      const carbonCreditsEffect = (carbonCredits * 1000000000) / energyInMJ;
      effectiveCI = Math.max(0, carbonIntensity - carbonCreditsEffect);
    }
    
    let additionalNAE_KTPA = (energyInMJ * effectiveCI) / 1_000_000_000;
    
    // Add to existing emissions (keep in KTPA like calculator)
    acc[index].CARBON_EMISSIONS += additionalNAE_KTPA;
    acc[index].CARBON_INTENSITY += current.CARBON_INTENSITY ?? 0;
    acc[index].QUANTITY += current.QUANTITY ?? 0;

    const prevVolumeWeighted = acc[index].VOLUME_WEIGHTED_AVG_CARBON_INT ?? 0;
    const volumeWeightedIncrement = quantity * effectiveCI; // Use effective CI
    acc[index].VOLUME_WEIGHTED_AVG_CARBON_INT = prevVolumeWeighted + volumeWeightedIncrement;

    const emissionKTPA = (energyInMJ * cat11Factor) / 1_000_000_000_000;
    acc[index].SCOPE3_CAT11_EMISSION = (acc[index].SCOPE3_CAT11_EMISSION ?? 0) + emissionKTPA;
  }

  const result = consolidatedData.reduce((accumulator: IDealSummary[], current) => {
    const existingYearIndex = accumulator.findIndex(
      (item: IDealSummary) => item.YEAR === current.YEAR,
    );
    if (existingYearIndex !== -1) {
      updateExistingYear(accumulator, existingYearIndex, current);
    } else {
      const nciByYear = netCarbonIntensityData.find(
        (item) => item.YEAR === current.YEAR,
      )?.CHANGED_REPORTED_GROUP_NCI;

      // Calculate T&S Products KPIs for first record of this year
      const quantity = current.QUANTITY ?? 0;
      let carbonIntensity = current.CARBON_INTENSITY ?? 0;
      const productName = current.PRODUCT_NAME ?? '';
      const conversionFactor = MJ_PER_KBBL[productName] || 0;
      const cat11Factor = cat11Factors[productName] || 0;

      // Apply carbon credits effect to carbon intensity (like calculator)
      const energyInMJ = quantity * conversionFactor;
      if (isTSProducts && current.CARBON_CREDIT === 1 && energyInMJ > 0) {
        const carbonCredits = current.CARBON_CREDIT_QUANTITY ?? 500; // KTPA
        const carbonCreditsEffect = (carbonCredits * 1000000000) / energyInMJ; // Convert KTPA to gCO2e/MJ
        carbonIntensity = Math.max(0, carbonIntensity - carbonCreditsEffect);
      }

      const volumeWeightedCI = quantity * carbonIntensity;
      const scope3Emission = (energyInMJ * cat11Factor) / (1000 * 1000 * 1000); // Convert to KTPA like calculator
      
      // For T&S Products, calculate NAE (Net Absolute Emissions) from energy and carbon intensity
      let carbonEmissions = current.CARBON_EMISSIONS ?? 0;
      if (isTSProducts && carbonEmissions === 0) {
        // Calculate NAE: Energy (MJ) × Effective Carbon Intensity (gCO2e/MJ) / 1e9 (to convert to KTPA)
        // Note: carbonIntensity already has carbon credits effect applied above
        carbonEmissions = (energyInMJ * carbonIntensity) / 1_000_000_000;
      }
      
      // Store in KTPA (same units as calculator)
      const carbonEmissionsKTPA = carbonEmissions;

      accumulator.push({
        YEAR: current.YEAR,
        OPERATED_SCOPE: current.OPERATED_SCOPE ?? 0,
        EQUITY_SCOPE: current.EQUITY_SCOPE ?? 0,
        CARBON_EMISSIONS: carbonEmissionsKTPA, // Store in KTPA like calculator
        CARBON_INTENSITY: current.CARBON_INTENSITY ?? 0,
        QUANTITY: current.QUANTITY ?? 0,
        CHANGED_REPORTED_GROUP_NCI: nciByYear ?? 0,
        // T&S Products: Store calculated KPI values
        VOLUME_WEIGHTED_AVG_CARBON_INT: volumeWeightedCI,
        SCOPE3_CAT11_EMISSION: scope3Emission,
      });
    }
    return accumulator;
  }, []);
  
  return result;
};

export const createKpiTablesData = (
  summedUpData: IDealSummary[],
  cols: IColumn[],
  commodity: string,
  evpName: string = '',
) => {
  const isTSProducts = evpName?.toLowerCase().replace(/[^a-z0-9]/g, '') === 'tsproducts';
  const currentFieldKeys = isTSProducts ? fieldKeys_TS : fieldKeys;
  const currentDataKeys = isTSProducts ? dataKeysSLMT_TS : dataKeys;

  const responseData: IKpiModal[] = [];

  currentFieldKeys.forEach((key, keyIndex) => {
    const responseObj: IKpiModal = {
      KPI: key,
      Total: '0',
    };

    cols.forEach((col) => {
      const year = Number(col.accessorKey);
      const dataForYear = summedUpData.find((item) => item.YEAR === year);

      if (!dataForYear) return;

      if (key === 'Avg Carbon Intensity**' || key === 'Volume-weighted Carbon Intensity**' || key === 'Volume-weighted Avg Carbon Intensity (KTPA)') {
        responseObj[year] = handleCarbonIntensity(
          dataForYear,
          key,
          keyIndex,
          currentDataKeys,
          commodity,
          isTSProducts,
          tenlakh,
          three,
        );
        responseObj['Total'] = null; // No total for intensity metrics
      } else if (key === 'NCI Impact (gCO2e/MJ)') {
        const value = Number(dataForYear[currentDataKeys[keyIndex] as keyof IDealSummary] ?? 0);
        responseObj[year] = convertInputToCommaSeparatedWithThreeDecimal(value.toFixed(three));
        responseObj['Total'] = null; // No total for NCI Impact
      } else {
        handleDefaultKpiValue(responseObj, dataForYear, keyIndex, currentDataKeys, year, three);
      }
    });

    responseData.push(responseObj);
  });

  return responseData;
};

function handleCarbonIntensity(
  dataForYear: IDealSummary,
  key: string,
  keyIndex: number,
  currentDataKeys: string[],
  commodity: string,
  isTSProducts: boolean,
  tenlakh: number,
  three: number,
) {
  let calculatedIntensity = dataForYear.CARBON_INTENSITY;

  if (commodity.toLowerCase() === 'power') {
    const dataYear = Number(dataForYear[currentDataKeys[keyIndex] as keyof IDealSummary] ?? 0);
    const quantityYear = Number(dataForYear.QUANTITY ?? 0);
    calculatedIntensity = (dataYear / quantityYear) * tenlakh;
  } else if (key === 'Volume-weighted Avg Carbon Intensity (KTPA)' && isTSProducts) {
    const weightedSum = dataForYear.VOLUME_WEIGHTED_AVG_CARBON_INT ?? 0;
    const totalQuantity = dataForYear.QUANTITY ?? 1;
    calculatedIntensity = weightedSum / totalQuantity;
    
    return convertInputToCommaSeparatedWithThreeDecimal(calculatedIntensity.toFixed(three));
  } else if (commodity.toLowerCase().includes('lng')) {
    // For SLMT (any LNG type: lng, cclng, biolng), use calculated carbon intensity like calculator
    // This matches the calculator logic that uses getCarbonIntensityForSLMT
    calculatedIntensity = calculateSLMTCarbonIntensity();
  }

  return convertInputToCommaSeparatedWithThreeDecimal(calculatedIntensity.toFixed(three));
}

function handleDefaultKpiValue(
  responseObj: IKpiModal,
  dataForYear: IDealSummary,
  keyIndex: number,
  currentDataKeys: string[],
  year: number,
  three: number,
) {
  const value = Number(dataForYear[currentDataKeys[keyIndex] as keyof IDealSummary] ?? 0);
  
  responseObj[year] = convertInputToCommaSeparatedWithThreeDecimal(value.toFixed(three));

  responseObj['Total'] = convertInputToCommaSeparatedWithThreeDecimal((Number(responseObj['Total']?.replace(/,/g, '') || '0') + value).toFixed(three));
}

export const getKpiTableData = (
  dealDetailsData: IDealDetails[],
  cols: IColumn[],
  netCarbonIntensityData: IGroupNCI[],
) => {
  const summedUpData = getSummedUpData(dealDetailsData, netCarbonIntensityData);
  const commodity = dealDetailsData[0]?.TRADE_COMMODITY_NAME ?? '';
  const evpName = dealDetailsData[0]?.EVP_NAME ?? '';
  
  // Create initial KPI table data
  let kpiTableData = createKpiTablesData(summedUpData, cols, commodity, evpName);
  
  // Apply calculator-style NCI calculations for SLMT and T&S Products
  kpiTableData = applyCalculatorNCILogic(kpiTableData, dealDetailsData, netCarbonIntensityData, evpName);
  
  return kpiTableData;
};

export const setUpKpiMessage = (
  peakAnnualEquity: number,
  efficiency: number,
  operatingModel: number,
  assetType: string,
) => {
  const isPerfStandardSignificant =
    peakAnnualEquity >= pointzerofive && peakAnnualEquity <= pointtwo;
  const isPerfStandardCritical = peakAnnualEquity > pointtwo;
  const isAssetBrownfield = assetType.toUpperCase() === BROWNFIELD;
  const isAssetGreenfield = assetType.toUpperCase() === GREENFIELD;
  // ORDER of messages
  /*
    - Equity Scope 1&2 Carbon (Peak Annual Emissions) < 0.050 mtCO2e
    - 0.050 mtCO2e > Equity Scope 1&2 Carbon (Peak Annual Emissions) < 0.200 mtCO2e
    - Equity Scope 1&2 Carbon (Peak Annual Emissions) > 0.200 mtCO2e

    - Operating Model in Step-1 = Investment SOV or Investment NOV
    - Operating Model in Step-1 = Lease

    - Performance Standard = Carbon Significant and Asset Type = Brownfield and Efficiency input in Step-1 >= 40%
    - Performance Standard = Carbon Significant and Asset Type = Greenfield and Efficiency input in Step-1 >= 42%
    - Performance Standard = Carbon Critical and Asset Type = Brownfield and Efficiency input in Step-1 >= 58%
    - Performance Standard = Carbon Critical and Asset Type = Greenfield and Efficiency input in Step-1 >= 60%

    - Performance Standard = Carbon Significant
    - Performance Standard = Carbon Critical
  */
  const message1 = getPerformanceStandardMessage(peakAnnualEquity);
  const message2 = getOperatingModelMessage(operatingModel);
  const message3 = getEfficiencyMessage(
    isPerfStandardSignificant,
    isPerfStandardCritical,
    isAssetBrownfield,
    isAssetGreenfield,
    efficiency,
  );
  const message4 =
    isPerfStandardCritical || isPerfStandardSignificant
      ? getContactInfo(isPerfStandardCritical)
      : '';

  return [message1, message2, message3, message4].filter((msg) => msg !== '');
};

// Calculator-style NCI calculation logic for SLMT and T&S Products
const applyCalculatorNCILogic = (
  kpiTableData: IKpiModal[],
  dealDetailsData: IDealDetails[],
  netCarbonIntensityData: IGroupNCI[],
  evpName: string,
): IKpiModal[] => {
  const normalizedEvp = (evpName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const isTSProducts = normalizedEvp === 'tsproducts';
  const isSLMT = normalizedEvp === 'slmt';
  
  if (!isTSProducts && !isSLMT) {
    return kpiTableData; // No changes needed for other EVPs
  }

  // Find the NCI Impact row
  const nciImpactRow = kpiTableData.find(row => row.KPI === 'NCI Impact (gCO2e/MJ)');
  if (!nciImpactRow) {
    return kpiTableData; // No NCI row to update
  }

  // For T&S Products, use consolidated data (same deduplication as other calculations)
  let consolidatedData = dealDetailsData;
  if (isTSProducts) {
    const uniqueRecords = new Map();
    dealDetailsData.forEach((item) => {
      const uniqueKey = `${item.YEAR}-${item.QUANTITY}-${item.PRODUCT_NAME}`;
      if (!uniqueRecords.has(uniqueKey)) {
        uniqueRecords.set(uniqueKey, item);
      }
    });
    consolidatedData = Array.from(uniqueRecords.values());
  }

  // Group deals by year for processing
  const dealsByYear: Record<number, IDealDetails[]> = {};
  for (const deal of consolidatedData) {
    if (!dealsByYear[deal.YEAR]) dealsByYear[deal.YEAR] = [];
    dealsByYear[deal.YEAR].push(deal);
  }

  // Apply NCI calculations for each year
  Object.keys(dealsByYear).forEach(yearStr => {
    const year = Number(yearStr);
    const deals = dealsByYear[year];
    
    if (isSLMT) {
      // SLMT NCI Impact calculation: (Combined Group + Deal NCI) - Baseline REPORTED_GROUP_NCI
      const groupData = netCarbonIntensityData.find((item) => item.YEAR === year);
      
      // Use original baseline NCI from API response (preserved before modification)
      const baselineNCI = groupData?.ORIGINAL_BASELINE_NCI || groupData?.REPORTED_GROUP_NCI;
      
      if (!baselineNCI) {
        return;
      }
      
      // Get deal volume in TBTU and convert to MJ
      const dealData = deals[0]; // Single deal for SLMT
      const volumeTBTU = dealData?.QUANTITY ?? 0;
      
      const dealEnergyMJ = (volumeTBTU / SLMT_CONVERSION_FACTORS.TBTU_HHV_TO_MT_LNG) * 
                          SLMT_CONVERSION_FACTORS.LNG_KG_TO_MJ_LHV * 1000000000;
      
      // Use carbon intensity from SLMT calculation (matches calculator logic)
      const dealCarbonIntensity = calculateSLMTCarbonIntensity();
      
      // Get baseline group data from API
      const groupEnergyMJ = groupData?.GROUP_VOLUME ?? 0;
      const groupNAE_MTPA = groupData?.NET_ABSOLUTE_EMISSIONS ?? 0;
      
      // Calculate deal NAE 
      const dealNAE_MTPA = (dealEnergyMJ * dealCarbonIntensity) / 1_000_000_000_000;
      
      // Calculate combined NCI: (Group NAE + Deal NAE) / (Group Energy + Deal Energy)
      const combinedNAE_MTPA = groupNAE_MTPA + dealNAE_MTPA;
      const combinedEnergy_MJ = groupEnergyMJ + dealEnergyMJ;
      const combinedNCI = combinedEnergy_MJ > 0 ? (combinedNAE_MTPA * 1_000_000_000_000) / combinedEnergy_MJ : 0;
      
      // NCI Impact = Combined NCI - Baseline NCI
      const nciImpact = combinedNCI - baselineNCI;
      
      // Update NCI Impact row with calculated value (3 decimal places for SLMT)
      nciImpactRow[year] = convertInputToCommaSeparatedWithThreeDecimal(nciImpact.toFixed(3));
      
    } else if (isTSProducts) {
      // T&S Products NCI calculation: Complex volume-weighted aggregation
      // Use centralized MJ_PER_KBBL mapping from constants
      // Calculate Deal Energy (MJ) and NAE (KTPA) from all products for this year
      let dealEnergyMJ = 0;
      let dealNAE_KTPA = 0;
      
      for (const deal of deals) {
        const commodity = deal.PRODUCT_NAME || deal.TRADE_COMMODITY_NAME || '';
        const volumeKBBL = deal.QUANTITY ?? 0;
        const carbonIntensity = deal.CARBON_INTENSITY ?? 0;
        const factor = MJ_PER_KBBL[commodity];
        
        if (factor && volumeKBBL !== undefined) {
          const energyMJ = volumeKBBL * factor;
          dealEnergyMJ += energyMJ;
          
          // Calculate NAE for this deal: Energy (MJ) × CI (gCO2e/MJ) / 1e9
          let nae_KTPA = (energyMJ * carbonIntensity) / 1_000_000_000;
          
          // Apply carbon credits if applicable
          if (deal.CARBON_CREDIT === 1) {
            const carbonReduction = deal.CARBON_CREDIT_QUANTITY ?? 500;
            nae_KTPA = Math.max(0, nae_KTPA - carbonReduction);
          }
          
          dealNAE_KTPA += nae_KTPA;
        }
      }
      
      // Keep in KTPA for NCI calculation (like calculator)
      const dealNAE_MTPA = dealNAE_KTPA / 1000; // Convert to MTPA only for NCI calculation
      
      // Get OP Group data from netCarbonIntensityData API
      const opData = netCarbonIntensityData.find((item) => item.YEAR === year);
      const opGroupNAE_MTPA = opData?.NET_ABSOLUTE_EMISSIONS ?? 0;
      const opGroupEnergy_MJ = opData?.GROUP_VOLUME ?? 0;
      
      // Use preserved baseline NCI to prevent data corruption
      const baselineNCI = opData?.REPORTED_GROUP_NCI;
      
      if (!baselineNCI) {
        return;
      }
      
      // Client's Formula: (OP Group NAE + Deal NAE) / (OP Group Energy + Deal Energy) - OP Group NCI
      const combinedNAE_MTPA = opGroupNAE_MTPA + dealNAE_MTPA;
      const combinedEnergy_MJ = opGroupEnergy_MJ + dealEnergyMJ;
      const combinedNCI = combinedEnergy_MJ > 0 ? (combinedNAE_MTPA * 1_000_000_000_000) / combinedEnergy_MJ : 0;
      const nciImpact = combinedNCI - baselineNCI;
      
      // Update NCI Impact row with calculated value (5 decimal places for T&S Products)
      nciImpactRow[year] = convertInputToCommaSeparatedWithThreeDecimal(nciImpact.toFixed(5));
    }
  });

  return kpiTableData;
};

const applyOperation = (firstVal: number, secondVal: number, operatingPlan: number | undefined) => {
  return operatingPlan === one ? firstVal - secondVal : firstVal + secondVal;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str || str.trim() === '') {
    return str;
  }
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // Always capitalize first word, or if it's not an article/preposition
      if (index === 0 || word !== 'of') {
        return word.charAt(0).toUpperCase() + word.slice(one);
      }
      return word; // Keep articles/prepositions lowercase
    })
    .join(' ');
};

export const calculateNCIData = (calculateNCIDataObj: {
  deltaVolumeInMj: number;
  year: number;
  netCarbonIntensityData: IGroupNCI[];
  commodityName: string | string[];
  carbonEmissionInMTPA?: number;
  operatingPlan: number | undefined;
}) => {
  const {
    deltaVolumeInMj,
    year,
    netCarbonIntensityData,
    commodityName,
    carbonEmissionInMTPA,
    operatingPlan,
  } = calculateNCIDataObj;
  const scenarioNciObjByYear = netCarbonIntensityData.filter((item) => item.YEAR === year)[0];
  const oldGroupVolume = scenarioNciObjByYear?.GROUP_VOLUME ?? 0;
  const oldNetAbsEmission = scenarioNciObjByYear?.NET_ABSOLUTE_EMISSIONS ?? 0;
  const newGroupVolume = applyOperation(oldGroupVolume, deltaVolumeInMj, operatingPlan);
  let newNetAbsEmission = oldNetAbsEmission;
  if (carbonEmissionInMTPA) {
    newNetAbsEmission = applyOperation(newNetAbsEmission, carbonEmissionInMTPA, operatingPlan);
  }
  const newNetAbsEmissionInGco2e = convertValue(newNetAbsEmission, 'mtpa', 'gco2e', commodityName);
  const newNetCarbonIntensity =
    newNetAbsEmissionInGco2e && newNetAbsEmissionInGco2e / newGroupVolume; // gcoe2/mj
  const reportedGroupNci = scenarioNciObjByYear?.REPORTED_GROUP_NCI;
  let diffOfBaseAndNewNci = 0;
  if (newNetCarbonIntensity) {
    diffOfBaseAndNewNci =
      operatingPlan === one
        ? reportedGroupNci - newNetCarbonIntensity
        : newNetCarbonIntensity - reportedGroupNci;
    } else {
    // eslint-disable-next-line no-console
    console.log(`[calculateNCIData] Year ${year}: newNetCarbonIntensity is falsy! Not calculating diff`);
  }
  const updatedData = {
    ...scenarioNciObjByYear,
    CHANGED_NET_ABSOLUTE_EMISSIONS: newNetAbsEmission,
    CHANGED_GROUP_VOLUME: newGroupVolume,
    CHANGED_REPORTED_GROUP_NCI: roundToDecimals(diffOfBaseAndNewNci, three),
    ORIGINAL_BASELINE_NCI: scenarioNciObjByYear.REPORTED_GROUP_NCI, // Preserve original baseline
    REPORTED_GROUP_NCI: scenarioNciObjByYear.REPORTED_GROUP_NCI, // Keep original baseline, not difference
  };
  return netCarbonIntensityData.map((item) => {
    if (item.YEAR === year) {
      return updatedData;
    }
    // For years NOT being calculated in this call, return them unchanged
    // This preserves any previously calculated CHANGED_REPORTED_GROUP_NCI values
    // and prevents resetting them back to the baseline
    //
    // CRITICAL FIX: Previously, the code would reset all non-matching years like this:
    //   return {
    //     ...item,
    //     CHANGED_NET_ABSOLUTE_EMISSIONS: NET_ABSOLUTE_EMISSIONS,
    //     CHANGED_GROUP_VOLUME: GROUP_VOLUME,
    //     CHANGED_REPORTED_GROUP_NCI: roundToDecimals(REPORTED_GROUP_NCI, three),  // ❌ BUG: Reset to baseline!
    //   };
    // 
    // This caused a cascading reset problem:
    // - Year 2026 calculated: CHANGED_REPORTED_GROUP_NCI = -67.658 ✓
    // - Year 2027 processing: resets 2026 back to 70.980 (baseline rounded) ✗
    // - Year 2028 processing: resets 2027 back to 70.326 (baseline rounded) ✗
    // - Year 2030 processing: resets 2028 back to 69.389 (baseline rounded) ✗
    // 
    // Result: Only the last year processed (2030) had the correct calculated value.
    // All previous years showed the baseline value instead of their calculations!
    // 
    // Solution: Never modify non-matching years. Just return them unchanged.
    return item;
  });
};
export const sumFieldsByYear = (deals: IDealDetails[]) => {
  const sumsByYear: {
    [key: number]: {
      YEAR: number;
      TRADE_COMMODITY_NAME: string;
      CARBON_EMISSIONS: number;
      QUANTITY_GWH: number;
    };
  } = {};

  deals.forEach((deal) => {
    const {
      YEAR,
      CARBON_EMISSIONS,
      CARBON_EMISSIONS_UNIT,
      TRADE_COMMODITY_NAME,
      QUANTITY,
      QUANTITY_UNIT,
    } = deal;
    const carbonEmissionInMTPA = convertValue(
      CARBON_EMISSIONS,
      CARBON_EMISSIONS_UNIT,
      'mtpa',
      TRADE_COMMODITY_NAME,
    );
    if (!sumsByYear[YEAR]) {
      sumsByYear[YEAR] = {
        YEAR,
        TRADE_COMMODITY_NAME,
        CARBON_EMISSIONS: 0,
        QUANTITY_GWH: 0,
      };
    }
    sumsByYear[YEAR].CARBON_EMISSIONS += carbonEmissionInMTPA ?? 0;
    sumsByYear[YEAR].QUANTITY_GWH += convertValue(
      QUANTITY,
      QUANTITY_UNIT,
      'GWh',
      TRADE_COMMODITY_NAME,
    ) as number;
  });

  return Object.values(sumsByYear);
};
const addNaOrMissingYearNci = (nciData: IGroupNCI[], years: number[]) => {
  // Iterate over the nciData array and update or add missing years
  const updatedNciData = nciData.map((item) => {
    // Check if the year is 2025 or 2030 and not in the years array
    if ([year2025, year2030].includes(item.YEAR) && !years.includes(item.YEAR)) {
      return {
        ...item,
        REPORTED_GROUP_NCI: notAvailable, // Set to -9999 to show NCI NA
      };
    }
    return item; // Return the original item if no changes are needed
  });

  // Add missing years to the updatedNciData array
  years.forEach((year) => {
    const dataExists = nciData.some((item) => item.YEAR === year);
    if (!dataExists) {
      updatedNciData.push({
        ...dummyNciData,
        YEAR: year,
      });
    }
  });
  return updatedNciData;
};
export const calculateImpactYearNCIDeals = (
  summedDealsByYear: {
    YEAR: number;
    TRADE_COMMODITY_NAME: string | string[];
    CARBON_EMISSIONS: number;
    QUANTITY_GWH: number;
  }[],
  nciData: IGroupNCI[],
  operatingPlan: number | undefined,
) => {
  const years = summedDealsByYear.map((deal) => deal.YEAR);
  let updatedNciData = addNaOrMissingYearNci(nciData, years);
  summedDealsByYear.forEach((deal) => {
    const { YEAR, TRADE_COMMODITY_NAME, QUANTITY_GWH, CARBON_EMISSIONS } = deal;
    const deltaVolumeInMj = convertValue(QUANTITY_GWH, 'GWh', `mj${YEAR}`, TRADE_COMMODITY_NAME);
    updatedNciData = calculateNCIData({
      deltaVolumeInMj: deltaVolumeInMj as number,
      year: YEAR,
      netCarbonIntensityData: updatedNciData,
      commodityName: TRADE_COMMODITY_NAME,
      carbonEmissionInMTPA: CARBON_EMISSIONS,
      operatingPlan,
    });
  });
  return updatedNciData;
};

// Get the appropriate year for the first row (shows 2025 through 2029, hidden from 2030 onwards)
const getFirstRowYear = (): number | null => {
  const currentYear = new Date().getFullYear();
  if (currentYear <= 2029) {
    return currentYear;
  }
  // From 2030 onwards, first row is hidden
  return null;
};

// Get the appropriate year for the second row (shows 2030 through 2030, increments from 2031 onwards)
const getSecondRowYear = (): number => {
  const currentYear = new Date().getFullYear();
  if (currentYear < year2030) {
    return year2030;
  }
  // From 2031 onwards, show the current year (incremented)
  return currentYear;
};

export const getImpactNciDataForTwoYears = (impactNciData: IGroupNCI[], dealDetailsData?: IDealDetails[], kpiTableData?: IKpiModal[]) => {
  const firstRowYear = getFirstRowYear();
  const secondRowYear = getSecondRowYear();

  const nciForFirstRow = firstRowYear
    ? impactNciData.find((nci) => nci.YEAR === firstRowYear)
    : null;
  const nciForSecondRow = impactNciData.find((nci) => nci.YEAR === secondRowYear);

  // Extract NCI Impact values - try KPI table first, then fall back to CHANGED_REPORTED_GROUP_NCI
  const extractNciValue = (year: number | null, nciRowData: IGroupNCI | null) => {
    if (!year) return 'NA';
    
    // Try to extract from KPI table first (for deals page)
    if (kpiTableData) {
      const nciImpactRow = kpiTableData.find(row => row.KPI === 'NCI Impact (gCO2e/MJ)');
      if (nciImpactRow) {
        const value = nciImpactRow[year];
        if (typeof value === 'string') {
          return value.replace(/,/g, '');
        }
      }
    }
    
    // Fall back to CHANGED_REPORTED_GROUP_NCI (for calculator page)
    if (nciRowData && nciRowData.CHANGED_REPORTED_GROUP_NCI !== undefined && nciRowData.CHANGED_REPORTED_GROUP_NCI !== notAvailable) {
      return nciRowData.CHANGED_REPORTED_GROUP_NCI.toString();
    }
    
    return 'NA';
  };

  // Use values from KPI table or CHANGED_REPORTED_GROUP_NCI
  const nciValueForFirstRow = extractNciValue(firstRowYear, nciForFirstRow || null);
  const nciForFirstRowUnit = !nciForFirstRow || nciForFirstRow?.CHANGED_REPORTED_GROUP_NCI === notAvailable ? 'NA' : nciForFirstRow?.GROUP_NCI_TARGET_UNIT;
  const nciValueForSecondRow = extractNciValue(secondRowYear, nciForSecondRow || null);
  const nciForSecondRowUnit = nciForSecondRow?.CHANGED_REPORTED_GROUP_NCI === notAvailable ? 'NA' : nciForSecondRow?.GROUP_NCI_TARGET_UNIT;

  return {
    nciValueForFirstRow: convertNegativeValueToZero(nciValueForFirstRow),
    nciValueForSecondRow: convertNegativeValueToZero(nciValueForSecondRow),
    nciForFirstRowUnit: nciForFirstRowUnit,
    nciForSecondRowUnit: nciForSecondRowUnit,
    firstRowYear,
    secondRowYear,
  };
};

export const convertNegativeValueToZero = (value: string | undefined) => {
  return value === '-0.000' ? '0.000' : value;
};

export const getColorByValue = (value: number) => {
  if (value === 0 || value === notAvailable) {
    return 'grey';
  } else if (value < 0) {
    return 'green';
  } else if (value > 0) {
    return 'red';
  } else {
    return 'grey';
  }
};

export const setColorByValueForKpiCard = (targetIndex: number, tableClass: string) => {
  const table = document.querySelector(`.${tableClass}`) as HTMLTableElement;
  const tr = table.querySelectorAll(`tbody tr:nth-child(${targetIndex})`)[0];
  (tr as HTMLTableCellElement).style.borderBottom = '1px solid #e0e0e0';

  const tds = table.querySelectorAll(`tbody tr:nth-child(${targetIndex + 1}) td`);
  tds.forEach((td) => {
    const cellValue = Number(td.textContent?.trim());
    const color = getColorByValue(cellValue);
    (td as HTMLTableCellElement).style.color = color;
    (td as HTMLTableCellElement).style.fontWeight = 'bold';
  });
};
